import React, { useState } from "react";
import styled from "styled-components";
import { Icon, IconContainer } from "../visuals/styledComponents";
import { CompassParagraph } from "../common/styledComponents";
import companyPlaceholder from "../../assets/svg/company_placeholder.svg";
import { formatReadable } from "../../utils/formattingUtils";
import { ElementContainer } from "../containers/styledComponents";
import { compassLightGreyBackground, greyText } from "../../styles";
import { ROUTES } from "../../routes/routeNames";
import { LoadingTableCell } from "./LoadingTableCell";

export enum CompanyLogoRoute {
  COMPANY_PROFILE = "COMPANY_PROFILE",
  COMPANY_ENGAGEMENTS = "COMPANY_ENGAGEMENTS",
}

const StyledContainer = styled(ElementContainer)`
  cursor: pointer;
`;

const StyledIcon = styled(Icon)`
  border-radius: 4px;
`;

export const CompanyLogo: React.FC<CompanyLogoProps> = ({
  name,
  logoUrl,
  companyId,
  size = "sm",
  isLoading = false,
  logoBg = compassLightGreyBackground,
  route = CompanyLogoRoute.COMPANY_PROFILE,
}) => {
  const [currentLogoUrl, setCurrentLogoUrl] = useState(logoUrl);

  const handleLogoError = (): void => {
    setCurrentLogoUrl(companyPlaceholder);
  };

  const handleLogoLoad = (
    event: React.SyntheticEvent<HTMLImageElement, Event>
  ): void => {
    const { naturalWidth, naturalHeight } = event.currentTarget;
    const minDimension = 30;
    const aspectRatio = naturalWidth / naturalHeight;
    const isValidSize =
      naturalWidth >= minDimension && naturalHeight >= minDimension;
    const isValidAspectRatio = aspectRatio >= 0.5 && aspectRatio <= 2;

    if (!isValidSize || !isValidAspectRatio) {
      handleLogoError();
    }
  };

  const SIZES: Sizes = {
    sm: {
      logoSize: 30,
      fontSize: 13,
    },
    lg: {
      logoSize: 37,
      fontSize: 16,
    },
    md: {
      logoSize: 33,
      fontSize: 15,
    },
  };

  const getRouteUrl = (): string | null => {
    if (!companyId) return null;

    switch (route) {
      case CompanyLogoRoute.COMPANY_PROFILE:
        return `${ROUTES.COMPANY_PROFILE}/${companyId}`;
      case CompanyLogoRoute.COMPANY_ENGAGEMENTS:
        const route = `${ROUTES.COMPANY_ENGAGEMENTS}/${companyId}`;
        console.log("Route URL:", route);
        return route;
      default:
        return null;
    }
  };

  return (
    <StyledContainer
      ai="center"
      ml={-15}
      onClick={(): void => {
        const url = getRouteUrl();
        if (url) window.open(url, "_blank", "noopener,noreferrer");
      }}
    >
      {
        <IconContainer
          size={SIZES[size].logoSize}
          p={1}
          bg={logoBg}
          br={10}
          isLoading={isLoading}
        >
          {!isLoading && currentLogoUrl && (
            <StyledIcon
              src={currentLogoUrl}
              alt={`${name} Logo`}
              onError={handleLogoError}
              onLoad={handleLogoLoad}
            />
          )}
        </IconContainer>
      }
      {isLoading ? (
        <LoadingTableCell dark={true} ml={1} />
      ) : (
        <CompassParagraph
          hoverUnderline={!!companyId}
          fontSize={SIZES[size].fontSize}
          bold={SIZES[size].bold}
          color={greyText}
          cursor={companyId ? "pointer" : "default"}
        >
          {formatReadable(name)}
        </CompassParagraph>
      )}
    </StyledContainer>
  );
};

export type Size = "sm" | "lg" | "md";

type Sizes = {
  [key in Size]: {
    logoSize: number;
    fontSize: number;
    bold?: boolean;
  };
};

export type CompanyLogoProps = {
  name: string;
  logoUrl: string;
  companyId?: string;
  size?: Size;
  isLoading?: boolean;
  logoBg?: string;
  route?: CompanyLogoRoute;
};
